import { select, call, put, takeEvery, getContext } from 'redux-saga/effects';
import * as actionTypes from 'constants/chromeExtension/actionTypes';
import { SPOOF_STARTED, SPOOF_STOPPED } from 'constants/spoofing';
import { USER_REPLACED } from 'actions/user';

import {
  selectHasCheckedForChromeExtension,
  selectChromeExtensionId,
} from 'reducers/chromeExtension';
import { buildDeepLinkToStepChild } from 'utils/steps';
import { selectFlow, selectFlows } from 'reducers/account/flows';
import { hasChrome } from 'helpers/chromeExtension';
import { getChromeExtensionVersion } from 'utils/chromeExtension';
import { promisaga } from 'utils/as-promised';
import { updateChromeExtensionStatus } from 'actions/chromeExtension';
import { ACCOUNT_SWITCHED } from 'actions/accounts';
import { reportError } from 'helpers/error-reporting';

export function* checkForChromeExtension(forceCheck = false) {
  if (!hasChrome()) {
    yield put(
      updateChromeExtensionStatus({
        hasChrome: false,
        hasExtensionInstalled: false,
      })
    );
  }
  const hasCheckedForChromeExtension = yield select(
    selectHasCheckedForChromeExtension
  );

  if (!forceCheck && hasCheckedForChromeExtension) {
    return;
  }

  const chromeExtensionId = yield select(selectChromeExtensionId);

  try {
    const chromeExtensionVersion = yield call(
      getChromeExtensionVersion,
      chromeExtensionId
    );

    yield put(
      updateChromeExtensionStatus({
        hasChrome: true,
        hasExtensionInstalled: true,
        version: chromeExtensionVersion,
      })
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    yield put(
      updateChromeExtensionStatus({
        hasChrome: true,
        hasExtensionInstalled: false,
      })
    );
  }
}

export function* setIsSpoofingAccountInChromeExtension(action) {
  const { type, payload: { id } = {} } = action;
  const isSpoofing = type === SPOOF_STARTED;
  if (isSpoofing && !id) {
    return;
  }

  const chromeExtensionId = yield select(selectChromeExtensionId);

  const spoofingData = {
    isSpoofingAccount: isSpoofing,
    spoofAccountId: id,
  };

  window.chrome.runtime.sendMessage(chromeExtensionId, {
    action: 'set-is-spoofing-account',
    value: JSON.stringify(spoofingData),
  });
}

export function* sendBuildMessageToChromeExtension(
  url,
  contentKey = null,
  options = {}
) {
  const chromeExtensionId = yield select(selectChromeExtensionId);

  const injectionData = {
    url,
    contentKey,
    options,
  };

  window.chrome.runtime.sendMessage(chromeExtensionId, {
    action: 'add-builder',
    value: JSON.stringify(injectionData),
  });
}

export function* startBuilderInChromeExtension(action) {
  const { url } = action.payload;
  yield call(sendBuildMessageToChromeExtension, url);
}

export function* editStepInChromeExtension(action) {
  const { stepGroupId, stepChildId } = action.payload;
  const stepGroup = yield select(selectFlow, stepGroupId);
  const allSteps = yield select(selectFlows);

  const url = buildDeepLinkToStepChild(stepGroup, stepChildId, allSteps);

  yield call(sendBuildMessageToChromeExtension, url, stepGroupId, {
    stepKey: stepChildId,
  });
}

function* logoutUserFromChromeExtension() {
  const chromeExtensionId = yield select(selectChromeExtensionId);

  window.chrome.runtime.sendMessage(chromeExtensionId, {
    action: 'appcues:log-out',
    value: JSON.stringify({
      source: 'webapp',
    }),
  });
}

export function* switchAccountInChromeExtension() {
  try {
    const auth = yield getContext('auth');
    const accountId = yield call(auth.getAccountId);
    // post-switch, the auth client has a token with an updated account id
    // builder needs this to know that the user is in new account
    const token = yield call(auth.getToken);
    const chromeExtensionId = yield select(selectChromeExtensionId);
    window.chrome.runtime.sendMessage(chromeExtensionId, {
      action: 'set-account',
      value: {
        accountId,
        token,
      },
    });
  } catch (error) {
    yield call(reportError, error);
  }
}

export default function* chromeExtension() {
  yield takeEvery(
    actionTypes.CHECK_FOR_CHROME_EXTENSION,
    checkForChromeExtension
  );
  yield takeEvery(
    actionTypes.EDIT_STEP_IN_CHROME_EXTENSION,
    promisaga(editStepInChromeExtension)
  );
  yield takeEvery(
    actionTypes.START_BUILDER_IN_CHROME_EXTENSION,
    startBuilderInChromeExtension
  );
  yield takeEvery(
    [SPOOF_STARTED, SPOOF_STOPPED],
    setIsSpoofingAccountInChromeExtension
  );
  yield takeEvery(USER_REPLACED, checkForChromeExtension, true);
  yield takeEvery(
    actionTypes.LOGOUT_USER_FROM_CHROME_EXTENSION,
    logoutUserFromChromeExtension
  );
  yield takeEvery(ACCOUNT_SWITCHED, switchAccountInChromeExtension);
}
